// TheoryTab lead sheet examples for the "Lead Sheet Examples" tab.
//
// The full corpus is ~1.9 GB across five CFG settings, two generation modes,
// seven control attributes and ten example songs, which is far past what the
// static host will take. Only the landing state is bundled with the app; see
// STAGED_COMBINATIONS below and scripts/stage_demo_assets.sh.
//
// Point REACT_APP_LEADSHEET_ASSET_BASE at a remote host holding the same
// directory layout to serve the whole corpus instead.

const ASSET_BASE = (process.env.REACT_APP_LEADSHEET_ASSET_BASE || '').replace(/\/$/, '');

export const REMOTE_ASSETS_CONFIGURED = ASSET_BASE !== '';

export const MODELS = {
  '3x_attr': {
    key: '3x_attr',
    displayName: 'MIDI-LLM, no CFG',
  },
  '3x_attr_cfg_3.0': {
    key: '3x_attr_cfg_3.0',
    displayName: 'MIDI-LLM, CFG = 3',
  },
  '3x_attr_cfg_5.0': {
    key: '3x_attr_cfg_5.0',
    displayName: 'MIDI-LLM, CFG = 5',
  },
  '3x_attr_cfg_7.0': {
    key: '3x_attr_cfg_7.0',
    displayName: 'MIDI-LLM, CFG = 7',
  },
  '3x_attr_cfg_10.0': {
    key: '3x_attr_cfg_10.0',
    displayName: 'MIDI-LLM, CFG = 10',
    supportedGenModes: ['infill_both'],
  },
};

export const GEN_MODES = {
  left_to_right: {
    key: 'left_to_right',
    displayName: 'From Scratch',
    description: 'Full generation from scratch',
  },
  infill_both: {
    key: 'infill_both',
    displayName: 'Infill',
    description: 'Infilling with MIDI context on both sides',
  },
};

export const CONTROL_ATTRS = {
  genre: {
    displayName: 'Genre',
    values: [
      'ambient_downtempo', 'asian_pop', 'classical_orchestral', 'country',
      'dance_electronic', 'experimental_avantgarde', 'folk_americana',
      'hip_hop_rap', 'holiday_specialty', 'jazz', 'latin', 'metal',
      'musical_theatre', 'pop', 'punk_hardcore', 'reggae_ska_dancehall',
      'rnb_soul_funk', 'rock', 'singer_songwriter', 'soundtrack_score',
    ],
  },
  mood: {
    displayName: 'Mood',
    values: [
      'aggressive', 'anthemic', 'brooding', 'celebratory', 'cinematic',
      'confident', 'dreamy', 'ethereal', 'euphoric', 'groovy', 'hopeful',
      'introspective', 'melancholic', 'mysterious', 'nostalgic', 'ominous',
      'playful', 'romantic', 'tender', 'uplifting_soft', 'warm', 'wistful',
    ],
  },
  attr_level_chord_difficulty: {
    displayName: 'Chord Difficulty',
    values: ['1', '2', '3', '4', '5', '6', '7'],
  },
  attr_level_chord_melody_tension: {
    displayName: 'Chord-Melody Tension',
    values: ['1', '2', '3', '4', '5', '6', '7'],
  },
  attr_level_chord_progression_novelty: {
    displayName: 'Progression Novelty',
    values: ['1', '2', '3', '4', '5', '6', '7'],
  },
  attr_level_melody_difficulty: {
    displayName: 'Melody Difficulty',
    values: ['1', '2', '3', '4', '5', '6', '7'],
  },
  attr_level_progression_bassline_motion: {
    displayName: 'Bassline Motion',
    values: ['1', '2', '3', '4', '5', '6', '7'],
  },
};

export const EXAMPLE_IDS = [
  39023, 39104, 39151, 39246, 39491, 39513, 39587, 39660, 39673, 39958,
];

export const DEFAULTS = {
  model: '3x_attr',
  genMode: 'left_to_right',
  controlAttr: 'genre',
  exampleId: 39673,
};

// What actually ships with the app. Anything outside this renders as
// unavailable rather than as a broken player.
const STAGED_COMBINATIONS = [
  {
    model: '3x_attr',
    genMode: 'left_to_right',
    controlAttr: 'genre',
    exampleIds: [39673],
  },
];

// True when the assets ship inside the app rather than coming from the remote
// host. The landing selection stays local even once a remote base is configured
// so that first paint never depends on the asset host being reachable.
export const isBundledLocally = ({ model, genMode, controlAttr, exampleId }) =>
  STAGED_COMBINATIONS.some((combo) => (
    combo.model === model
    && combo.genMode === genMode
    && combo.controlAttr === controlAttr
    && combo.exampleIds.includes(exampleId)
  ));

export const isStaged = (params) => REMOTE_ASSETS_CONFIGURED || isBundledLocally(params);

// Partial availability checks, used to grey out options in the selectors.
export const isModelStaged = (model) =>
  REMOTE_ASSETS_CONFIGURED || STAGED_COMBINATIONS.some((c) => c.model === model);

export const isGenModeStaged = (genMode) =>
  REMOTE_ASSETS_CONFIGURED || STAGED_COMBINATIONS.some((c) => c.genMode === genMode);

export const isControlAttrStaged = (controlAttr) =>
  REMOTE_ASSETS_CONFIGURED || STAGED_COMBINATIONS.some((c) => c.controlAttr === controlAttr);

export const isExampleIdStaged = (exampleId) =>
  REMOTE_ASSETS_CONFIGURED || STAGED_COMBINATIONS.some((c) => c.exampleIds.includes(exampleId));

export const getAvailableModels = (genMode) =>
  Object.values(MODELS).filter(
    (m) => !m.supportedGenModes || m.supportedGenModes.includes(genMode)
  );

export const getAttributeValues = (controlAttr) => CONTROL_ATTRS[controlAttr]?.values || [];

export const getAttributeDisplayName = (controlAttr) =>
  CONTROL_ATTRS[controlAttr]?.displayName || controlAttr;

export const formatValueName = (value, controlAttr) => {
  if (controlAttr.startsWith('attr_level')) return `Level ${value}`;
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Flattened layout written by scripts/stage_demo_assets.sh, used identically by
// the bundled copy and by the remote host:
//   /leadsheet/<model>/<mode>/<attr>/<value>/<exampleId>/
const buildDir = (params) => {
  const { model, genMode, controlAttr, attributeValue, exampleId } = params;
  const base = isBundledLocally(params) ? '' : ASSET_BASE;
  return `${base}/leadsheet/${model}/${genMode}/${controlAttr}/${attributeValue}/${exampleId}`;
};

export const getAudioPath = (params) => `${buildDir(params)}/gen.mp3`;
export const getNotesPath = (params) => `${buildDir(params)}/notes.json`;
export const getPromptPath = (params) => `${buildDir(params)}/prompt.txt`;
export const getResponsePath = (params) => `${buildDir(params)}/response.json`;
